'use client'

import { useCallback, useEffect, useState } from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Coins, Save, Users, Tag, Plus, Pencil, Trash2, Crown } from 'lucide-react'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ProductSelector } from '@/components/form/product-selector'

import { useStore, useUpdateStoreCreditSettings, type StoreDetails } from '@/lib/api/stores'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useStorePromotions, useDeletePromotions } from '@/lib/api/promotions'
import { getActionLabels, isVipDiscount } from '@/lib/promotion-discount'
import { ClubOffersTab } from './ClubOffersTab'
import { CreateProductDialog } from './CreateProductDialog'
import { ProductEditDialog } from './ProductEditDialog'
import { VipDiscountDialog, type VipDiscountPromotion } from './VipDiscountDialog'
import { ClubSetupWizard } from './ClubSetupWizard'
import {
    useClubByStoreId,
    useCreateClub,
    useUpdateClub,
    useDefineMembershipProductTrigger,
} from '@/lib/api/clubs'

const VIP_BADGE_CLASS =
    'flex-shrink-0 rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900 dark:text-amber-300'

/** Shows one badge per discount action; collapses to "+N" with a tooltip when more than three. */
const DiscountBadges = ({ labels }: { labels: string[] }) => {
    if (labels.length === 0) return null
    if (labels.length <= 3) {
        return (
            <div className="flex flex-shrink-0 items-center gap-1">
                {labels.map((l, i) => (
                    <span key={i} className={VIP_BADGE_CLASS}>
                        {l}
                    </span>
                ))}
            </div>
        )
    }
    return (
        <div className="flex flex-shrink-0 items-center gap-1">
            {labels.slice(0, 3).map((l, i) => (
                <span key={i} className={VIP_BADGE_CLASS}>
                    {l}
                </span>
            ))}
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className={`${VIP_BADGE_CLASS} cursor-default`}>+{labels.length - 3}</span>
                    </TooltipTrigger>
                    <TooltipContent>
                        <div className="space-y-0.5">
                            {labels.map((l, i) => (
                                <div key={i}>{l}</div>
                            ))}
                        </div>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    )
}

/**
 * Membership tab — club status toggle + membership product trigger.
 * There is no Save button: the club row is created lazily on the first edit
 * and both controls auto-save.
 */
const MembershipTab = ({ storeId }: { storeId: string }) => {
    const { t } = useTranslation('club')
    const [createProductOpen, setCreateProductOpen] = useState(false)
    const [editProductOpen, setEditProductOpen] = useState(false)
    const [vipDiscountOpen, setVipDiscountOpen] = useState(false)
    const [editingVip, setEditingVip] = useState<VipDiscountPromotion | null>(null)
    const [deletingVipId, setDeletingVipId] = useState<string | null>(null)
    const deletePromotions = useDeletePromotions()

    const { data: club, isLoading } = useClubByStoreId({ storeId }, !!storeId)
    const { data: store } = useStore(storeId)
    const currency = store?.baseCurrency || 'USD'
    const { data: promoData, refetch: refetchPromotions } = useStorePromotions({ storeId, pageSize: 100 }, !!storeId)
    const vipDiscounts = (promoData?.promotions ?? []).filter((p) => isVipDiscount(p, club?.productId))
    const createClubMutation = useCreateClub()
    const updateClubMutation = useUpdateClub()
    const defineProductTriggerMutation = useDefineMembershipProductTrigger()

    const ensureClubId = useCallback(async (): Promise<string | null> => {
        if (club?.id) return club.id
        if (!storeId) return null
        const [created] = await createClubMutation.mutateAsync({ storeId, active: false })
        return created.clubId
    }, [club, storeId, createClubMutation])

    const handleActiveChange = useCallback(
        async (checked: boolean) => {
            const clubId = await ensureClubId()
            if (!clubId) return
            updateClubMutation.mutate({ clubId, data: { active: checked } })
        },
        [ensureClubId, updateClubMutation],
    )

    const handleProductChange = useCallback(
        async (productId: string) => {
            const clubId = await ensureClubId()
            if (!clubId) return
            defineProductTriggerMutation.mutate({
                clubId,
                storeId,
                productId: productId || null,
            })
        },
        [ensureClubId, storeId, defineProductTriggerMutation],
    )

    const isActive = club?.active ?? false

    if (isLoading) {
        return (
            <div className="space-y-6">
                <Card>
                    <CardHeader>
                        <CardTitle>{t('config.status.label')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between">
                            <Skeleton className="h-6 w-20" />
                            <Skeleton className="h-6 w-11 rounded-full" />
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Users className="h-5 w-5" />
                            {t('config.membershipProduct.title')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Skeleton className="h-10 w-full" />
                    </CardContent>
                </Card>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Club status (enable/disable) — auto-saves */}
            <Card>
                <CardHeader>
                    <CardTitle>{t('config.status.label')}</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center justify-between">
                        <Badge variant={isActive ? 'green' : 'gray'}>
                            {isActive ? t('config.status.active') : t('config.status.inactive')}
                        </Badge>
                        <Switch id="club-status" checked={isActive} onCheckedChange={handleActiveChange} />
                    </div>
                </CardContent>
            </Card>

            {/* Membership product trigger — auto-saves */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        {t('config.membershipProduct.title')}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <ProductSelector
                        storeId={storeId}
                        productId={club?.productId || undefined}
                        onProductChange={handleProductChange}
                        onEdit={() => setEditProductOpen(true)}
                        label={t('config.membershipProduct.label')}
                        helpText={t('config.membershipProduct.helpText')}
                        action={
                            <Button
                                type="button"
                                variant="outline"
                                className="shrink-0 gap-2"
                                onClick={() => setCreateProductOpen(true)}
                            >
                                <Plus className="h-4 w-4" />
                                {t('config.membershipProduct.createProduct')}
                            </Button>
                        }
                    />
                </CardContent>
            </Card>

            {/* VIP discount — minimal creator, everything prefilled */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Tag className="h-5 w-5" />
                        {t('vipDiscount.cardTitle')}
                    </CardTitle>
                    <p className="text-muted-foreground text-sm">{t('vipDiscount.cardDescription')}</p>
                </CardHeader>
                <CardContent className="space-y-3">
                    {vipDiscounts.length > 0 && (
                        <div className="space-y-2">
                            {vipDiscounts.map((p) => {
                                return (
                                    <div
                                        key={p.id}
                                        className="flex items-center gap-3 rounded-lg border bg-muted/50 p-3"
                                    >
                                        <Tag className="text-primary h-4 w-4 flex-shrink-0" />
                                        <p className="min-w-0 flex-1 truncate text-sm font-medium">{p.name}</p>
                                        <DiscountBadges labels={getActionLabels(p.actions, currency)} />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 flex-shrink-0 p-0"
                                            title={t('vipDiscount.edit')}
                                            onClick={() => {
                                                setEditingVip({ id: p.id, name: p.name, actions: p.actions })
                                                setVipDiscountOpen(true)
                                            }}
                                        >
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 flex-shrink-0 p-0"
                                            title={t('vipDiscount.delete.confirm')}
                                            onClick={() => setDeletingVipId(p.id)}
                                        >
                                            <Trash2 className="h-4 w-4 text-red-500" />
                                        </Button>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                    <Button
                        type="button"
                        variant="outline"
                        className="gap-2"
                        disabled={!club?.productId}
                        onClick={() => {
                            setEditingVip(null)
                            setVipDiscountOpen(true)
                        }}
                    >
                        <Plus className="h-4 w-4" />
                        {t('vipDiscount.create')}
                    </Button>
                    {!club?.productId && (
                        <p className="text-muted-foreground text-xs">{t('vipDiscount.needProduct')}</p>
                    )}
                </CardContent>
            </Card>

            <CreateProductDialog
                storeId={storeId}
                open={createProductOpen}
                onOpenChange={setCreateProductOpen}
                onCreated={handleProductChange}
            />

            <VipDiscountDialog
                storeId={storeId}
                productId={club?.productId ?? ''}
                promotion={editingVip}
                open={vipDiscountOpen}
                onOpenChange={(o) => {
                    setVipDiscountOpen(o)
                    if (!o) setEditingVip(null)
                }}
                onSaved={refetchPromotions}
            />

            <AlertDialog open={!!deletingVipId} onOpenChange={(o) => !o && setDeletingVipId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('vipDiscount.delete.title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('vipDiscount.delete.description')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('vipDiscount.delete.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => {
                                if (deletingVipId) {
                                    deletePromotions.mutate(
                                        { ids: [deletingVipId], storeId },
                                        { onSuccess: () => refetchPromotions() },
                                    )
                                }
                                setDeletingVipId(null)
                            }}
                        >
                            {t('vipDiscount.delete.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <ProductEditDialog
                storeId={storeId}
                productId={club?.productId ?? null}
                open={editProductOpen}
                onOpenChange={setEditProductOpen}
            />
        </div>
    )
}

const InlineInput = ({
    value,
    onChange,
    min = '0',
    step = '1',
    disabled,
}: {
    value: number
    onChange: (n: number) => void
    min?: string
    step?: string
    disabled?: boolean
}) => (
    <Input
        type="number"
        min={min}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-7 w-16 px-2 text-center text-sm font-semibold [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
    />
)

const InlineCurrency = ({
    value,
    onChange,
    currency,
    disabled,
}: {
    value: number
    onChange: (n: number) => void
    currency: string
    disabled?: boolean
}) => (
    <span className="inline-flex items-center gap-1">
        <Input
            type="number"
            min="0.01"
            step="0.01"
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(Number(e.target.value))}
            className="h-7 w-16 px-2 text-center text-sm font-semibold [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        <span className="text-muted-foreground text-xs font-medium">{currency}</span>
    </span>
)

const CreditSettingsForm = ({ store }: { store: StoreDetails }) => {
    const { t } = useTranslation('stores')
    const updateCreditSettingsMutation = useUpdateStoreCreditSettings()

    const [enabled, setEnabled] = useState(store.creditSettings?.enabled ?? false)
    const [defaultCreditsPerRebill, setDefaultCreditsPerRebill] = useState(store.creditSettings?.defaultCreditsPerRebill ?? 100)
    const [ratioCredits, setRatioCredits] = useState(store.creditSettings?.creditRatio?.credits ?? 100)
    const [ratioBaseAmount, setRatioBaseAmount] = useState((store.creditSettings?.creditRatio?.baseAmount ?? 100) / 100)
    const [rewardEnabled, setRewardEnabled] = useState(store.creditSettings?.creditRewardRatio?.enabled ?? false)
    const [rewardCredits, setRewardCredits] = useState(store.creditSettings?.creditRewardRatio?.credits ?? 100)
    const [rewardBaseAmount, setRewardBaseAmount] = useState((store.creditSettings?.creditRewardRatio?.baseAmount ?? 1000) / 100)

    const hasChanges =
        enabled !== (store.creditSettings?.enabled ?? false) ||
        defaultCreditsPerRebill !== (store.creditSettings?.defaultCreditsPerRebill ?? 100) ||
        ratioCredits !== (store.creditSettings?.creditRatio?.credits ?? 100) ||
        ratioBaseAmount !== (store.creditSettings?.creditRatio?.baseAmount ?? 100) / 100 ||
        rewardEnabled !== (store.creditSettings?.creditRewardRatio?.enabled ?? false) ||
        rewardCredits !== (store.creditSettings?.creditRewardRatio?.credits ?? 100) ||
        rewardBaseAmount !== (store.creditSettings?.creditRewardRatio?.baseAmount ?? 1000) / 100

    const isSaving = updateCreditSettingsMutation.isPending

    const handleSave = () => {
        updateCreditSettingsMutation.mutate({
            storeId: store.id,
            enabled,
            defaultCreditsPerRebill,
            creditRatio: {
                credits: ratioCredits,
                baseAmount: Math.round(ratioBaseAmount * 100),
            },
            creditRewardRatio: {
                enabled: rewardEnabled,
                credits: rewardCredits,
                baseAmount: Math.round(rewardBaseAmount * 100),
            },
        })
    }

    const formatBaseAmount = (amount: number, currency: string) => {
        return `${amount.toFixed(2)} ${currency}`
    }

    // Live example values
    const exampleSpend = 50
    const earnedFromOrder =
        rewardEnabled && rewardBaseAmount > 0 ? Math.round(rewardCredits * (exampleSpend / rewardBaseAmount)) : 0
    const valueOfCredits = (credits: number) => (ratioCredits > 0 ? (credits / ratioCredits) * ratioBaseAmount : 0)
    const coin = <Coins className="-mt-0.5 inline h-4 w-4 text-amber-500" />

    return (
        <div className="flex flex-col space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div>
                    <h2 className="text-lg font-semibold sm:text-xl">{t('creditSettings.title')}</h2>
                    <p className="text-muted-foreground text-sm">{t('creditSettings.description')}</p>
                </div>
                <Button onClick={handleSave} disabled={!hasChanges || isSaving} className="gap-2">
                    <Save className="h-4 w-4" />
                    {isSaving ? t('creditSettings.saving') : t('creditSettings.save')}
                </Button>
            </div>

            <Card>
                <CardContent className="space-y-6">
                    {/* Status row */}
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400">
                                <Coins className="h-6 w-6" />
                            </div>
                            <div>
                                <h3 className="font-semibold">{t('creditSettings.enable.title')}</h3>
                                <p className="text-muted-foreground text-sm">
                                    {enabled
                                        ? t('creditSettings.enable.enabledDescription')
                                        : t('creditSettings.enable.disabledDescription')}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-2">
                            <Badge variant={enabled ? 'green' : 'gray'}>
                                {enabled ? t('creditSettings.enable.statusLabel') : t('creditSettings.disabled.title')}
                            </Badge>
                            <Switch id="credit-enabled" checked={enabled} onCheckedChange={setEnabled} />
                        </div>
                    </div>

                    {enabled ? (
                        <div className="space-y-6 border-t pt-6">
                            {/* Earning */}
                            <section className="space-y-3">
                                <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                                    {t('creditSettings.fun.earning')}
                                </p>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
                                    <span>
                                        {t('creditSettings.fun.rebillBefore')}
                                    </span>
                                    <InlineInput value={defaultCreditsPerRebill} onChange={setDefaultCreditsPerRebill} />
                                    <span className="font-medium">
                                        {coin} {t('creditSettings.fun.credits')}
                                    </span>
                                </div>
                                <div
                                    className={`flex flex-wrap items-center gap-x-3 gap-y-2 text-sm ${rewardEnabled ? '' : 'opacity-60'}`}
                                >
                                    <Switch
                                        id="reward-enabled"
                                        checked={rewardEnabled}
                                        onCheckedChange={setRewardEnabled}
                                    />
                                    <span>{t('creditSettings.fun.rewardBefore')}</span>
                                    <InlineInput
                                        value={rewardCredits}
                                        min="1"
                                        onChange={setRewardCredits}
                                        disabled={!rewardEnabled}
                                    />
                                    <span className="font-medium">
                                        {coin}{' '}
                                        {t('creditSettings.fun.rewardMid')}
                                    </span>
                                    <InlineCurrency
                                        value={rewardBaseAmount}
                                        onChange={setRewardBaseAmount}
                                        currency={store.baseCurrency}
                                        disabled={!rewardEnabled}
                                    />
                                    <span>{t('creditSettings.fun.rewardAfter')}</span>
                                </div>
                            </section>

                            {/* Redeeming */}
                            <section className="space-y-3">
                                <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                                    {t('creditSettings.fun.redeeming')}
                                </p>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
                                    <InlineInput value={ratioCredits} min="1" onChange={setRatioCredits} />
                                    <span className="font-medium">
                                        {coin} {t('creditSettings.fun.credits')}
                                    </span>
                                    <span className="text-muted-foreground text-lg font-medium">=</span>
                                    <InlineCurrency
                                        value={ratioBaseAmount}
                                        onChange={setRatioBaseAmount}
                                        currency={store.baseCurrency}
                                    />
                                    <span>
                                        {t('creditSettings.fun.offAtCheckout')}
                                    </span>
                                </div>
                            </section>

                            {/* Live example */}
                            <section className="rounded-xl border bg-muted/40 p-4">
                                <p className="text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wide">
                                    {t('creditSettings.fun.example')}
                                </p>
                                <ul className="space-y-1 text-sm">
                                    <li>
                                        {t('creditSettings.fun.exampleRebill')}:{' '}
                                        <span className="font-semibold">+{defaultCreditsPerRebill}</span> {coin} ≈{' '}
                                        <span className="font-semibold">
                                            {formatBaseAmount(valueOfCredits(defaultCreditsPerRebill), store.baseCurrency)}
                                        </span>
                                    </li>
                                    {rewardEnabled && (
                                        <li>
                                            {t('creditSettings.fun.exampleOrder', {
                                                amount: formatBaseAmount(exampleSpend, store.baseCurrency),
                                            })}
                                            : <span className="font-semibold">+{earnedFromOrder}</span> {coin} ≈{' '}
                                            <span className="font-semibold">
                                                {formatBaseAmount(valueOfCredits(earnedFromOrder), store.baseCurrency)}
                                            </span>
                                        </li>
                                    )}
                                </ul>
                            </section>
                        </div>
                    ) : (
                        <p className="text-muted-foreground border-t pt-6 text-sm">
                            {t('creditSettings.disabled.description')}
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

const CreditsTab = ({ storeId }: { storeId: string }) => {
    const { data: store } = useStore(storeId)

    if (!store) {
        return (
            <div className="flex flex-col space-y-4 sm:space-y-6">
                <Skeleton className="h-10 w-1/3" />
                <Card>
                    <CardHeader>
                        <Skeleton className="h-6 w-1/4" />
                        <Skeleton className="h-4 w-2/3" />
                    </CardHeader>
                    <CardContent>
                        <Skeleton className="h-8 w-full" />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <Skeleton className="h-6 w-1/4" />
                        <Skeleton className="h-4 w-1/2" />
                    </CardHeader>
                    <CardContent>
                        <Skeleton className="h-8 w-full" />
                    </CardContent>
                </Card>
            </div>
        )
    }

    return <CreditSettingsForm store={store} />
}

/**
 * Club settings page — served at /settings/credits.
 * Merges the former Storefront → Club page (Membership) with the credit
 * settings (Credits) into a single two-tab surface.
 */
const CLUB_TABS = ['membership', 'credits', 'offers'] as const

const ClubSettingsPageInner = () => {
    const { t } = useTranslation('club')
    const { storeRoutes, navigate, id: storeId } = useStoreNavigate()
    const { tab } = useParams<{ tab?: string }>()

    const activeTab = tab && (CLUB_TABS as readonly string[]).includes(tab) ? tab : 'membership'

    // Keep the URL on a concrete tab segment (e.g. /settings/club → /settings/club/membership).
    useEffect(() => {
        if (!tab || !(CLUB_TABS as readonly string[]).includes(tab)) {
            navigate(storeRoutes.CLUB_SETTINGS_TAB('membership'), { replace: true })
        }
    }, [tab, navigate, storeRoutes])

    // First-visit setup wizard: club disabled and no membership product yet.
    const { data: club, isLoading: clubLoading } = useClubByStoreId({ storeId: storeId || '' }, !!storeId)
    const [wizardOpen, setWizardOpen] = useState(false)
    const dismissKey = `club-setup-dismissed-${storeId}`
    const firstVisit = !clubLoading && (!club || (!club.active && !club.productId))

    useEffect(() => {
        if (firstVisit && storeId && !localStorage.getItem(dismissKey)) {
            setWizardOpen(true)
        }
    }, [firstVisit, storeId, dismissKey])

    const handleWizardOpenChange = (o: boolean) => {
        setWizardOpen(o)
        if (!o && storeId) localStorage.setItem(dismissKey, '1')
    }

    return (
        <div className="flex flex-col space-y-4 sm:space-y-6">
            {/* Page header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div>
                    <h1 className="text-xl font-semibold sm:text-2xl">{t('config.title')}</h1>
                    <p className="text-muted-foreground text-sm">{t('config.description')}</p>
                </div>
                <Button variant="outline" className="gap-2" onClick={() => setWizardOpen(true)}>
                    <Crown className="h-4 w-4" />
                    {t('setup.reopen')}
                </Button>
            </div>

            <ClubSetupWizard storeId={storeId || ''} open={wizardOpen} onOpenChange={handleWizardOpenChange} />

            <Tabs value={activeTab} onValueChange={(v) => navigate(storeRoutes.CLUB_SETTINGS_TAB(v))}>
                <TabsList>
                    <TabsTrigger value="membership" className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {t('tabs.membership')}
                    </TabsTrigger>
                    <TabsTrigger value="credits" className="flex items-center gap-2">
                        <Coins className="h-4 w-4" />
                        {t('tabs.credits')}
                    </TabsTrigger>
                    <TabsTrigger value="offers" className="flex items-center gap-2">
                        <Tag className="h-4 w-4" />
                        {t('tabs.offers')}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="membership" className="space-y-6">
                    <MembershipTab storeId={storeId || ''} />
                </TabsContent>

                <TabsContent value="credits" className="space-y-6">
                    <CreditsTab storeId={storeId || ''} />
                </TabsContent>

                <TabsContent value="offers" className="space-y-6">
                    <ClubOffersTab storeId={storeId || ''} />
                </TabsContent>
            </Tabs>
        </div>
    )
}

export const ClubSettingsPage = withRequiredPermission(ClubSettingsPageInner, PERMISSIONS.STORES_MANAGE)
